import Dexie, { type Table } from 'dexie';

import type { ReaderEvent } from '../track';

/** The name is also listed in `@shared/storage`'s app-key guard. */
export const EVENT_LOG_DB_NAME = 'cf-events';

export interface EventLogRow {
	/** Assigned by the database, in the order events were recorded. A cursor is one of these. */
	seq?: number;
	/** The event's own `event_ts`, promoted to a column so expiry is an index range. */
	event_ts: number;
	/** Approximate serialized size, cached at write time for the byte budget. */
	bytes: number;
	/**
	 * Whether the reader was sharing analytics when this was recorded. An
	 * event recorded while sharing was off is never sent, even after the
	 * reader turns sharing back on; it is still here for their own stats.
	 */
	shared: boolean;
	event: ReaderEvent;
}

export interface EventLogCursorRow {
	/** A consumer's name: a destination (`ga4`, `matomo`, ...) or a projection. */
	consumer: string;
	/** The `seq` of the last row this consumer has dealt with. */
	seq: number;
}

/**
 * The reader-event log: a database of its own, the third beside the
 * downloads database (`DexieDb`) and `cf-observability`, with a different
 * owner and lifetime from both. Clearing or corrupting one cannot affect
 * another, and analytics consent never decides whether engineering learns
 * the app broke (ADR 0012).
 */
/**
 * What the device remembers so that "the first time" stays the first time:
 * how much of an article has been read, and which pages or articles of an
 * issue have been seen (`reading-memory.ts`). Kept after the raw events
 * expire, and not part of the reader's stats: clearing those does not make
 * an article "complete" a second time.
 */
export interface ReadingMemoryRow {
	/** `a:<issue>:<article>` or `i:<issue>:<layout>`. */
	key: string;
	dwellMs?: number;
	depth?: number;
	units?: Array<number>;
	done: boolean;
}

/** One calendar day of the reader's own reading, in the device's time zone. */
export interface StatsDayRow {
	/** `YYYY-MM-DD`. */
	day: string;
	readMs: number;
	listenedMs: number;
	articlesCompleted: number;
	issuesCompleted: number;
	/**
	 * The same four figures by hour of the day (0 to 23, device time), for
	 * the hours that have any. An event belongs to the hour it was recorded
	 * in, so a reading that ran across the hour counts in the one it ended
	 * in. Absent on a day recorded before this existed.
	 */
	hours?: Record<number, StatsHourFigures>;
}

export interface StatsHourFigures {
	readMs: number;
	listenedMs: number;
	articlesCompleted: number;
	issuesCompleted: number;
}

export interface StatsIssueRow {
	issueId: number;
	title?: string;
	readMs: number;
	/**
	 * How far through the issue: pages (replica) or articles (responsive)
	 * seen, of how many it has. An issue can be read in both layouts, and
	 * pages and articles are different units, so each layout is counted on
	 * its own (`byLayout`, which also says which units, so one seen twice
	 * counts once) and these two are the layout the reader is furthest
	 * through. Progress never goes down because the layout changed.
	 */
	unitsSeen: number;
	unitsTotal: number;
	byLayout?: Partial<
		Record<'replica' | 'responsive', { units: Array<number>; total: number }>
	>;
	/** What `issue_complete` stated, kept as a floor for the two above. */
	completedUnits?: { seen: number; total: number };
	completedAt?: number;
	lastReadAt: number;
}

export interface StatsArticleRow {
	issueId: number;
	articleId: number;
	title?: string;
	readMs: number;
	maxDepth: number;
	completedAt?: number;
	lastReadAt: number;
}

export class EventLogDb extends Dexie {
	events!: Table<EventLogRow, number>;
	cursors!: Table<EventLogCursorRow, string>;
	readingMemory!: Table<ReadingMemoryRow, string>;
	statsDays!: Table<StatsDayRow, string>;
	statsIssues!: Table<StatsIssueRow, number>;
	statsArticles!: Table<StatsArticleRow, [number, number]>;

	constructor() {
		super(EVENT_LOG_DB_NAME);
		this.version(1).stores({
			events: '++seq, event_ts',
			cursors: 'consumer',
		});
		// Version 2 adds what is derived from the log and kept after it
		// expires: the reading memory and the reader's own stats
		// (`@entities/reader-stats`). The log's tables are unchanged.
		this.version(2).stores({
			events: '++seq, event_ts',
			cursors: 'consumer',
			readingMemory: 'key',
			statsDays: 'day',
			statsIssues: 'issueId, lastReadAt',
			statsArticles: '[issueId+articleId], issueId, lastReadAt',
		});
	}
}

export const eventLogDb = new EventLogDb();
